import { TrashIcon } from "@heroicons/react/outline";
import { CopyIcon } from "@radix-ui/react-icons";
import { Maximize2, MoreVerticalIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

interface MessageActionsProps {
  onCopy: () => void;
  onMaximize?: () => void;
  onDelete: () => void;
  disabled?: boolean;
  readonly?: boolean;
  className?: string;
  /** Whether to show the Delete action. Defaults to true. */
  showDelete?: boolean;
  /** Hide actions by default and reveal them when the parent `.group` is hovered/focused. */
  showOnGroupHover?: boolean;
  /** Show compact, high-priority actions outside the dropdown on larger screens. */
  showResponsivePriorityActions?: boolean;
}

/**
 * MessageActions component provides progressive disclosure for message controls:
 * optional inline priority actions on larger screens + a dropdown for full actions.
 */
const MessageActions = ({
  onCopy,
  onMaximize,
  onDelete,
  disabled = false,
  readonly = false,
  className,
  showDelete = true,
  showOnGroupHover = false,
  showResponsivePriorityActions = false,
}: MessageActionsProps) => {
  const hoverVisibilityClass = showOnGroupHover
    ? "opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus-within:opacity-100"
    : "";

  const hasMaximizeAction = Boolean(onMaximize);
  const hasDeleteAction = !readonly && showDelete;
  const totalActionsCount = 1 + (hasMaximizeAction ? 1 : 0) + (hasDeleteAction ? 1 : 0);
  const inlineLgActionsCount = 1 + (hasDeleteAction ? 1 : 0);
  const inlineXlActionsCount = inlineLgActionsCount + (hasMaximizeAction ? 1 : 0);

  let dropdownResponsiveVisibilityClass = "";
  if (showResponsivePriorityActions) {
    if (totalActionsCount === inlineLgActionsCount) {
      dropdownResponsiveVisibilityClass = "lg:hidden";
    } else if (totalActionsCount === inlineXlActionsCount) {
      dropdownResponsiveVisibilityClass = "xl:hidden";
    }
  }

  return (
    <div className={cn("flex items-center gap-0.5", className)}>
      {showResponsivePriorityActions && (
        <div className={cn("hidden items-center gap-0.5 lg:flex", hoverVisibilityClass)}>
          <BasicTooltip content="Copy">
            <Button
              variant="ghost"
              size="tinyIcon"
              disabled={disabled}
              className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
              onClick={onCopy}
            >
              <CopyIcon className="h-4 w-4" />
              <span className="sr-only">Copy message</span>
            </Button>
          </BasicTooltip>

          {hasMaximizeAction && onMaximize && (
            <BasicTooltip content="Maximize">
              <Button
                variant="ghost"
                size="tinyIcon"
                disabled={disabled}
                className="hidden h-8 w-8 p-0 text-gray-500 hover:text-gray-700 xl:inline-flex"
                onClick={onMaximize}
              >
                <Maximize2 className="h-4 w-4" />
                <span className="sr-only">Maximize message</span>
              </Button>
            </BasicTooltip>
          )}

          {hasDeleteAction && (
            <BasicTooltip content="Delete">
              <Button
                variant="ghost"
                size="tinyIcon"
                disabled={disabled}
                className="h-8 w-8 p-0 text-gray-500 hover:text-red-600"
                onClick={onDelete}
              >
                <TrashIcon className="h-4 w-4" />
                <span className="sr-only">Delete message</span>
              </Button>
            </BasicTooltip>
          )}
        </div>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="tinyIcon"
            disabled={disabled}
            className={cn("h-8 w-8 p-0", hoverVisibilityClass, dropdownResponsiveVisibilityClass)}
          >
            <MoreVerticalIcon className="h-4 w-4" />
            <span className="sr-only">Message actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[160px]">
          <DropdownMenuItem onSelect={onCopy}>
            <CopyIcon className="h-4 w-4" />
            Copy
          </DropdownMenuItem>
          {onMaximize && (
            <DropdownMenuItem onSelect={onMaximize}>
              <Maximize2 className=" h-4 w-4" />
              Maximize
            </DropdownMenuItem>
          )}
          {!readonly && showDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onDelete} className="text-red-600 focus:text-red-600">
                <TrashIcon className=" h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default MessageActions;
